/**
 * Earnings Chart Component
 * Bar chart showing monthly earnings breakdown
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Custom tooltip with white text
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
        <p className="text-slate-300 text-sm mb-1">{label}</p>
        <p className="text-white font-semibold">
          Earnings: £{payload[0].value.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
        </p>
      </div>
    );
  }
  return null;
};

const BAR_RADIUS = [4, 4, 0, 0];
const CHART_CONTAINER_STYLE = { width: '100%', height: 256 };
const CHART_MARGIN_LONG = { top: 10, right: 5, left: 0, bottom: 20 };
const CHART_MARGIN_SHORT = { top: 10, right: 5, left: 0, bottom: 0 };

const EarningsChart = ({ monthlyData, title }) => {
  // Check if labels are long (e.g., tax year labels like "2025/2026")
  const hasLongLabels = monthlyData.some(d => d.month && d.month.length > 5);
  
  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader>
        <CardTitle className="text-white text-sm sm:text-base">{title || 'Earnings by Month'}</CardTitle>
      </CardHeader>
      <CardContent className="px-2 sm:px-6">
        <div style={CHART_CONTAINER_STYLE}>
          <ResponsiveContainer>
            <BarChart data={monthlyData} margin={hasLongLabels ? CHART_MARGIN_LONG : CHART_MARGIN_SHORT}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis 
                dataKey="month" 
                stroke="#94a3b8" 
                fontSize={hasLongLabels ? 9 : 11}
                angle={hasLongLabels ? -35 : 0}
                textAnchor={hasLongLabels ? 'end' : 'middle'}
                height={hasLongLabels ? 50 : 30}
                interval={0}
              />
              <YAxis 
                stroke="#94a3b8" 
                fontSize={10}
                tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                width={45}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="earnings" radius={BAR_RADIUS}>
                {monthlyData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.earnings > 0 ? '#10b981' : '#334155'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default EarningsChart;
